import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { trackNavigationClicked } from '@zola/tracking-contracts/src/tracking';
import { transformNavigationLevel } from 'util/trackNavigationClickedHelpersV2';
import { getHomestoreHref } from 'util/getHomestoreHref';
import { getBabyHref } from 'util/getBabyHref';
import featureFlags from 'util/featureFlags';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { getUserContext } from 'selectors/user';
import {
  getSearchedLocation,
  LocationOrAllMarketsType,
  deafultAllMarkets,
} from 'util/vendorSearchUtils';
import experimentFlags, { MARKETPLACE_CLP_LP } from 'util/experimentFlags';
import CloseIcon from '../../iconsV3/CloseIcon';
import {
  Container,
  Overlay,
  Close,
  SearchButton,
  StyledSearchIcon,
  StyledArrowIcon,
  BackToPrimaryCategoriesBtn,
  bottomBorder,
  FindCouple,
  LinkWrapper,
  BottomLinksWrapper,
} from './MobileNavMenu.styles';

import {
  getPrimaryPlan,
  primaryAdvice,
  PrimaryNavItemType,
  PrimaryNavType,
  SecondaryNavType,
} from '../../preauthNavData';
import MobileNavMenuLinkGroup from './MobileNavMenuLinkGroup';
import MobileNavMenuLinkGroupSub from './MobileNavMenuLinkGroup/MobileNavMenuLinkGroupSub';
import SignUpLogin from './SignUpLogin';

export type MobileNavMenuProps = {
  isMenuOpen?: boolean;
  primaryLinkId: PrimaryNavIdentifier;
  toggleSearch: () => void;
  toggleMenu: () => void;
};

const MobileNavMenu = ({
  isMenuOpen,
  primaryLinkId,
  toggleSearch,
  toggleMenu,
}: MobileNavMenuProps): JSX.Element => {
  const [menu, setMenu] = useState<PrimaryNavType | undefined>();
  const [menuItemIndex, setMenuItemIndex] = useState(0);
  const menuItem: PrimaryNavItemType | undefined = useMemo(
    () => menu?.nav[menuItemIndex],
    [menu?.nav, menuItemIndex]
  );
  const [subMenu, setSubMenu] = useState<SecondaryNavType | undefined>();
  const findACouple = 'Find a couple';
  const userContext = useSelector(getUserContext);
  const [searchedLocation, setSearchedLocation] =
    useState<LocationOrAllMarketsType>(deafultAllMarkets);
  const useVendorSearchUrl =
    experimentFlags.getFlag(MARKETPLACE_CLP_LP, userContext) &&
    window.location.pathname === '/wedding-vendors';

  const handleLocationChange = () => {
    setSearchedLocation(getSearchedLocation());
  };

  const handleAllBtnClick = () => {
    setSubMenu(undefined);
  };

  const handleTrackNavigationClicked = () => {
    trackNavigationClicked({
      business_category: 'UNATTRIBUTED',
      business_unit: 'UNATTRIBUTED',
      navigation_level_1: transformNavigationLevel(findACouple),
      navigation_level_1_position: 3,
      navigation_type: 'GLOBAL',
    });
  };

  useEffect(() => {
    // Open expert advice submenu when on expert advice pages
    if (primaryLinkId === PrimaryNavIdentifier.ADVICE) {
      const index = primaryAdvice.nav.findIndex((item) => item.id === PrimaryNavIdentifier.ADVICE);
      setMenu(primaryAdvice);
      setMenuItemIndex(index);
      setSubMenu(primaryAdvice.nav[index].subnav);
    }
  }, [primaryLinkId]);

  useEffectOnce(() => {
    setSearchedLocation(getSearchedLocation());

    if (typeof window !== 'undefined') {
      window.addEventListener('SEARCHED_LOCATION_CHANGE', handleLocationChange);
    }

    return (): void => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('SEARCHED_LOCATION_CHANGE', handleLocationChange);
      }
    };
  });

  return (
    <Fragment>
      <Overlay isMenuOpen={isMenuOpen} onClick={toggleMenu} />
      <Container isMenuOpen={isMenuOpen}>
        <Close aria-label="Close" type="button" onClick={toggleMenu}>
          <CloseIcon width={24} height={24} showTitle={false} />
        </Close>
        {menu && menuItem && subMenu ? (
          <Fragment>
            <BackToPrimaryCategoriesBtn onClick={handleAllBtnClick}>
              <StyledArrowIcon direction="left" />
              Main menu
            </BackToPrimaryCategoriesBtn>
            <MobileNavMenuLinkGroupSub
              menu={menu}
              menuItem={menuItem}
              menuItemIndex={menuItemIndex}
              subMenu={subMenu}
            />
          </Fragment>
        ) : (
          <Fragment>
            <div css={bottomBorder}>
              <SearchButton onClick={toggleSearch}>
                <StyledSearchIcon height={20} width={20} />
                Find gifts, vendors, couples...
              </SearchButton>
            </div>
            <div css={bottomBorder}>
              <SignUpLogin />
            </div>
            {[
              getPrimaryPlan(false, useVendorSearchUrl ? searchedLocation : undefined),
              primaryAdvice,
            ].map((linkGroups, index) => (
              <MobileNavMenuLinkGroup
                key={index}
                css={bottomBorder}
                linkGroups={linkGroups}
                setMenu={setMenu}
                setMenuItemIndex={setMenuItemIndex}
                setSubMenu={setSubMenu}
              />
            ))}
            <FindCouple href="/search/wedding-registry" onClick={handleTrackNavigationClicked}>
              {findACouple}
            </FindCouple>
          </Fragment>
        )}
        <BottomLinksWrapper>
          <LinkWrapper>
            Become a Zola{' '}
            <LinkV2 subtle inline href="/for-vendors" target="_blank" rel="noopener noreferrer">
              vendor
            </LinkV2>
          </LinkWrapper>
          <LinkWrapper>
            Just here to shop? Explore{' '}
            <LinkV2 subtle inline href={getHomestoreHref(featureFlags.getEnvironment())}>
              Zola Home
            </LinkV2>
          </LinkWrapper>
          <LinkWrapper>
            Start a baby registry with{' '}
            <LinkV2 subtle inline href={getBabyHref(featureFlags.getEnvironment())}>
              Zola Baby
            </LinkV2>
          </LinkWrapper>
        </BottomLinksWrapper>
      </Container>
    </Fragment>
  );
};

export default MobileNavMenu;
